import i18next from 'i18next'
import { formatPercentage } from './string'

/**
 * VAT number validation rules for EU countries. Each country has a specific format
 * for VAT numbers, and this object defines regular expressions to validate those formats.
 * The keys are the VAT/VIES country prefixes that appear at the start of EU VAT numbers,
 * and the values are regular expressions that match valid VAT numbers for that prefix.
 *
 * In most cases these prefixes correspond to the ISO 3166-1 alpha-2 country codes, but
 * there are exceptions defined by the VAT system itself (for example Greece uses `EL`
 * as its VAT prefix instead of the ISO code `GR`).
 *
 * Note: This validation only checks the format of the VAT number, not its validity or existence.
 * Also some formats may require additional checks (like checksum validation) which are not covered
 * by these regex patterns.
 */
const VAT_RULES: Record<string, RegExp> = {
  AT: /^ATU\d{8}$/, // Austria
  BE: /^BE[01]\d{9}$/, // Belgium (Modern 10-digit)
  BG: /^BG\d{9,10}$/, // Bulgaria
  CY: /^CY\d{8}[A-Z]$/, // Cyprus
  CZ: /^CZ\d{8,10}$/, // Czech Republic
  DE: /^DE\d{9}$/, // Germany
  DK: /^DK\d{8}$/, // Denmark
  EE: /^EE\d{9}$/, // Estonia
  EL: /^EL\d{9}$/, // Greece
  ES: /^ES[A-Z0-9]\d{7}[A-Z0-9]$/, // Spain
  FI: /^FI\d{8}$/, // Finland
  FR: /^FR[A-Z0-9]{2}\d{9}$/, // France
  HR: /^HR\d{11}$/, // Croatia
  HU: /^HU\d{8}$/, // Hungary
  IE: /^IE(\d{7}[A-Z\d]{1,2}|[A-Z\d+*]\d{5,7}[A-Z])$/, // Ireland
  IT: /^IT\d{11}$/, // Italy
  LT: /^LT(\d{9}|\d{12})$/, // Lithuania
  LU: /^LU\d{8}$/, // Luxembourg
  LV: /^LV\d{11}$/, // Latvia
  MT: /^MT\d{8}$/, // Malta
  NL: /^NL\d{9}B\d{2}$/, // Netherlands
  PL: /^PL\d{10}$/, // Poland
  PT: /^PT\d{9}$/, // Portugal
  RO: /^RO\d{2,10}$/, // Romania
  SE: /^SE\d{12}$/, // Sweden
  SI: /^SI\d{8}$/, // Slovenia
  SK: /^SK\d{10}$/ // Slovakia
}

export function isValidEuVatNumberFormat(value: string) {
  const countryCode = value.substring(0, 2).toUpperCase()
  const rule = VAT_RULES[countryCode]

  if (!rule) return false
  return rule.test(value)
}

export function translateValidationErrors(errors: Record<string, string | string[]>) {
  return Object.keys(errors).reduce((acc: Record<string, Array<string | string[] | null>>, key) => {
    const match = key.match(/^(.+)\[(\d+)\]$/)
    if (match) {
      const [_, field, index] = match

      if (!acc[field]) {
        acc[field] = [] as Array<string | string[] | null>
      }

      if (Array.isArray(errors[key])) {
        acc[field][parseInt(index)] = errors[key].map((error: string) =>
          i18next.t(error, { ns: 'validation', field: i18next.t(field, { ns: 'common' }) })
        )
      } else {
        acc[field][parseInt(index)] = [
          i18next.t(errors[key], { ns: 'validation', field: i18next.t(field, { ns: 'common' }) })
        ]
      }

      return acc
    }

    if (Array.isArray(errors[key])) {
      acc[key] = errors[key].map((error: string) => translateValidationError(key, error))
    } else if (typeof errors[key] === 'string') {
      acc[key] = [translateValidationError(key, errors[key])]
    }

    return acc
  }, {})
}

function translateValidationError(field: string, message: string) {
  const [error, ...params] = message.split(':')

  if (params.length > 0 && !isNaN(parseFloat(params[0]))) {
    const translatedField = i18next.t(field, { ns: 'common' })
    switch (error) {
      case 'mustBeGreaterThan':
        return mustBeGreaterThan(translatedField, parseFloat(params[0]))
      case 'mustBeGreaterThanOrEqualTo':
        return mustBeGreaterThanOrEqualTo(translatedField, parseFloat(params[0]))
      case 'mustBeLessThan':
        return mustBeLessThan(translatedField, parseFloat(params[0]))
      case 'mustBeLessThanOrEqualTo':
        return mustBeLessThanOrEqualTo(translatedField, parseFloat(params[0]))
      case 'relayOnThresholdMustBeLessThan':
        return relayOnThresholdMustBeLessThan(parseFloat(params[0]))
      case 'relayOffThresholdMustBeGreaterThan':
        return relayOffThresholdMustBeGreaterThan(parseFloat(params[0]))
    }
  }

  return i18next.t(error, { ns: 'validation', field: i18next.t(field, { ns: 'common' }) })
}

export function mustBeString(field: string) {
  return i18next.t('mustBeString', {
    ns: 'validation',
    field
  })
}

export function mustBeStringOrNull(field: string) {
  return i18next.t('mustBeStringOrNull', {
    ns: 'validation',
    field
  })
}

export function mustBeNumber(field: string) {
  return i18next.t('mustBeNumber', {
    ns: 'validation',
    field
  })
}

export function mustBeInteger(field: string) {
  return i18next.t('mustBeInteger', {
    ns: 'validation',
    field
  })
}

export function mustBeFloat(field: string) {
  return i18next.t('mustBeFloat', {
    ns: 'validation',
    field
  })
}

export function mustBeBoolean(field: string) {
  return i18next.t('mustBeBoolean', {
    ns: 'validation',
    field
  })
}

export function isRequired(field: string) {
  return i18next.t('isRequired', {
    ns: 'validation',
    field
  })
}

export function mustBeLessThanCharacters(field: string, max: number) {
  return i18next.t('mustBeLessThanCharacters', {
    ns: 'validation',
    field,
    max
  })
}

export function mustBeGreaterThan(field: string, min: number) {
  return i18next.t('mustBeGreaterThan', {
    ns: 'validation',
    field,
    min
  })
}

export function mustBeGreaterThanOrEqualTo(field: string, min: number) {
  return i18next.t('mustBeGreaterThanOrEqualTo', {
    ns: 'validation',
    field,
    min
  })
}

export function mustBeLessThan(field: string, max: number) {
  return i18next.t('mustBeLessThan', {
    ns: 'validation',
    field,
    max
  })
}

export function mustBeLessThanOrEqualTo(field: string, max: number) {
  return i18next.t('mustBeLessThanOrEqualTo', {
    ns: 'validation',
    field,
    max
  })
}

export function mustBePositive(field: string) {
  return i18next.t('mustBePositive', {
    ns: 'validation',
    field: field
  })
}

export function relayOnThresholdMustBeLessThan(value: number) {
  return i18next.t('relayOnThresholdMustBeLessThan', {
    ns: 'validation',
    value: formatPercentage(value)
  })
}

export function relayOffThresholdMustBeGreaterThan(value: number) {
  return i18next.t('relayOffThresholdMustBeGreaterThan', {
    ns: 'validation',
    value: formatPercentage(value)
  })
}

export function circularReference(field: string) {
  return i18next.t('circularReference', {
    ns: 'validation',
    field
  })
}

export function mustBePowerSource(field: string) {
  return i18next.t('mustBePowerSource', {
    ns: 'validation',
    field
  })
}
